import { useEffect, useState } from 'react';
import { featuresAPI } from '../services/api';

/**
 * Server-driven feature flags (GET /api/features).
 *
 * The flags live in the SERVER's env (ENABLE_CIRCLES=..., ENABLE_COACHES=...),
 * not in REACT_APP_*,
 * so flipping one needs a restart rather than a client rebuild. The trade-off is
 * that the first paint happens before the answer arrives, so:
 *   - the last known payload is mirrored to localStorage and used synchronously
 *     on the next boot (no flash of a feature that's switched off), and
 *   - the fallback matches each flag's server-side default: fail-OPEN for
 *     default-on features (circles — a network blip never hides a shipped
 *     feature) and fail-CLOSED for opt-in ones (coaches is off unless
 *     ENABLE_COACHES=true, so the fallback hides it too).
 *
 * @returns {{ circles: boolean, coaches: boolean }}
 */

const CACHE_KEY = 'sophia_features';
const DEFAULTS = { circles: true, coaches: false };

let memo = null;      // resolved payload, shared across hook instances
let inflight = null;  // in-flight request, shared across hook instances

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : null;
  } catch (e) {
    return null;
  }
}

function writeCache(features) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(features));
  } catch (e) {
    /* private mode / quota — the in-memory memo still holds for this session */
  }
}

function load() {
  if (memo) return Promise.resolve(memo);
  if (!inflight) {
    inflight = featuresAPI.getFeatures()
      .then((data) => {
        memo = { ...DEFAULTS, ...(data || {}) };
        writeCache(memo);
        return memo;
      })
      .catch(() => readCache() || DEFAULTS)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function useFeatures() {
  const [features, setFeatures] = useState(() => memo || readCache() || DEFAULTS);

  useEffect(() => {
    let alive = true;
    load().then((f) => { if (alive) setFeatures(f); });
    return () => { alive = false; };
  }, []);

  return features;
}

export default useFeatures;
